import { ROOT_CONTEXT, SpanStatusCode, trace, type Span } from '@opentelemetry/api';
import type { NavigationFailure, RouteLocationNormalized } from 'vue-router';
import { setNavigationContext } from './navigationContext';
import { getTracer } from './tracer';

// The page counts as loaded once the route has resolved and nothing has been in
// flight for this long. The cap stops a polling endpoint holding the span open.
const SETTLE_DEBOUNCE_MS = 500;
const MAX_PAGE_LOAD_MS = 30_000;

let span: Span | null = null;
let inFlight = 0;
let routeResolved = false;
let lastActivityAt = 0;
let lastRoutePattern: string | null = null;
let settleTimer: number | null = null;
let capTimer: number | null = null;

const clearTimers = (): void => {
    if (settleTimer != null) window.clearTimeout(settleTimer);
    if (capTimer != null) window.clearTimeout(capTimer);
    settleTimer = null;
    capTimer = null;
};

const finish = (reason: string): void => {
    if (span == null) return;

    span.setAttribute('page_load.settled_by', reason);
    // End at the last observed activity, not now, so the debounce window below
    // isn't added to every page's reported load time.
    span.end(reason === 'idle' && lastActivityAt > 0 ? lastActivityAt : undefined);

    span = null;
    clearTimers();
    setNavigationContext(null);
};

const maybeSettle = (): void => {
    if (span == null || !routeResolved) return;

    if (settleTimer != null) {
        window.clearTimeout(settleTimer);
        settleTimer = null;
    }

    if (inFlight > 0) return;

    settleTimer = window.setTimeout(() => finish('idle'), SETTLE_DEBOUNCE_MS);
};

// The matched route pattern ('/administrator/employee/:employmentId/:employeeId'),
// never the resolved path — a span name per employee id would be unqueryable.
const routePattern = (to: RouteLocationNormalized): string =>
    to.matched.at(-1)?.path ?? to.path;

/** Opens the page-load span. A redirect re-runs beforeEach, which closes the previous one. */
export const startPageLoad = (to: RouteLocationNormalized, from: RouteLocationNormalized): void => {
    finish('superseded');

    lastRoutePattern = routePattern(to);

    inFlight = 0;
    routeResolved = false;
    lastActivityAt = Date.now();

    span = getTracer().startSpan(routePattern(to), {
        attributes: {
            'route.pattern': routePattern(to),
            'route.to': to.fullPath,
            'route.from': from.fullPath,
            'route.name': String(to.name ?? 'unknown'),
        },
    }, ROOT_CONTEXT);

    setNavigationContext(trace.setSpan(ROOT_CONTEXT, span));
    capTimer = window.setTimeout(() => finish('timeout'), MAX_PAGE_LOAD_MS);
};

/** The route has resolved; the span still waits for in-flight requests to drain. */
export const resolvePageLoad = (failure: NavigationFailure | void): void => {
    if (span == null) return;

    if (failure != null) {
        span.setAttribute('route.failure_type', failure.type);
        span.setStatus({ code: SpanStatusCode.ERROR, message: failure.message });
    }

    routeResolved = true;
    lastActivityAt = Date.now();
    maybeSettle();
};

export const trackRequestStart = (): void => {
    inFlight += 1;
    lastActivityAt = Date.now();
    maybeSettle();
};

export const trackRequestEnd = (): void => {
    inFlight = Math.max(0, inFlight - 1);
    lastActivityAt = Date.now();
    maybeSettle();
};

/** The page an action was triggered from, for spans that outlive the page-load span. */
export const currentRoutePattern = (): string | null => lastRoutePattern;

/** Closes any open span on logout so it isn't dropped unexported. */
export const abortPageLoad = (): void => finish('shutdown');
